import React, { useEffect, useState, useCallback, useRef } from 'react'
import { GridApi, ColDef } from 'ag-grid-community'
import { useGridConfigurationStore } from '@/stores/grid-configuration.store'
import { exportAsXls } from '@/utils/xlsx-export'
import { CreateViewModal } from './CreateViewModal'
import { ViewsModal } from './ViewsModal'
import { DeleteViewModal } from './DeleteViewModal'
import { Checkbox } from '@/components/ui/Checkbox'
import cloneDeep from 'lodash-es/cloneDeep'
import { FloppyDisk, ArrowClockwise, Download, PushPin } from 'phosphor-react'

// Helper function to remove deprecated suppressMenu property from column definitions
const removeSuppressMenu = (colDef: ColDef): ColDef => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { suppressMenu: _suppressMenu, ...cleanedColDef } = colDef as ColDef & {
    suppressMenu?: boolean
  }
  return cleanedColDef
}

const removeSuppressMenuFromArray = (colDefs: ColDef[]): ColDef[] => {
  return colDefs.map(removeSuppressMenu)
}

interface AGTableColumnsProps {
  collection: string
  gridApi: GridApi
}

export const AGTableColumns: React.FC<AGTableColumnsProps> = ({ collection, gridApi }) => {
  const {
    getCollectionState,
    updateGridConfiguration,
    saveGridConfiguration,
    fetchGridConfiguration,
  } = useGridConfigurationStore()

  const [columns, setColumns] = useState<ColDef[]>([])
  const [isAllSelected, setIsAllSelected] = useState(true)
  const [selectedView, setSelectedView] = useState('par défaut')
  const [views, setViews] = useState<{ title: string; selected: boolean }[]>([])
  const [showCreateView, setShowCreateView] = useState(false)
  const [showViews, setShowViews] = useState(false)
  const [showDeleteView, setShowDeleteView] = useState(false)
  const [viewToDelete, setViewToDelete] = useState<string | null>(null)
  const [showColumnsMenu, setShowColumnsMenu] = useState(false)
  const [showPinMenu, setShowPinMenu] = useState(false)
  const [gridContentHeight, setGridContentHeight] = useState(0)
  const [gridBottomPosition, setGridBottomPosition] = useState(0)
  const columnsMenuRef = useRef<HTMLDivElement>(null)
  const pinMenuRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (columnsMenuRef.current && !columnsMenuRef.current.contains(target)) {
        setShowColumnsMenu(false)
      }
      if (pinMenuRef.current && !pinMenuRef.current.contains(target)) {
        setShowPinMenu(false)
      }
    }

    if (showColumnsMenu || showPinMenu) {
      // Use setTimeout to avoid immediate closure when opening
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 0)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showColumnsMenu, showPinMenu])

  const refreshColumns = useCallback(() => {
    const cols = gridApi
      .getAllGridColumns()
      .filter(item => item.getColDef().headerName)
      .map(value => ({
        ...value.getColDef(),
        colId: value.getColId(),
        pinned: value.getPinned(),
        sort: value.getSort(),
        hide: !value.isVisible(),
        width: value.getActualWidth(),
      }))
    const cleanedCols = removeSuppressMenuFromArray(cols)
    setColumns(cloneDeep(cleanedCols))
    setIsAllSelected(cleanedCols.every(column => !column.hide))

    // Update grid content height and bottom position
    const gridElement = document.querySelector('.ag-theme-alpine')
    if (gridElement) {
      const gridContainer = gridElement as HTMLElement
      const headerHeight = 48 // Default header height
      const paginationBar = gridContainer.querySelector('.ag-paging-panel') as HTMLElement
      const paginationBarHeight = paginationBar ? paginationBar.offsetHeight : 56
      const contentHeight = gridContainer.clientHeight - headerHeight - paginationBarHeight
      setGridContentHeight(contentHeight)

      // Calculate bottom position relative to viewport
      const gridRect = gridContainer.getBoundingClientRect()
      setGridBottomPosition(gridRect.bottom - paginationBarHeight)
    }
  }, [gridApi])

  useEffect(() => {
    fetchGridConfiguration().then(() => {
      setupColumns()
      const collectionState = getCollectionState(collection)
      if (collectionState) {
        const savedCols = collectionState.state[collectionState.selected] || []
        const cleanedCols = removeSuppressMenuFromArray(savedCols)
        setColumns(cleanedCols)
        setSelectedView(collectionState.selected)
        setViews(
          Object.keys(collectionState.state).map(key => ({
            title: key,
            selected: collectionState.selected === key,
          }))
        )
      } else {
        createDefaultView()
      }
      refreshColumns()
    })

    const refreshColumnEvents: Array<'columnVisible' | 'columnPinned' | 'columnMoved'> = [
      'columnVisible',
      'columnPinned',
      'columnMoved',
    ]
    refreshColumnEvents.forEach(event => {
      gridApi.addEventListener(event, refreshColumns)
    })

    return () => {
      refreshColumnEvents.forEach(event => {
        gridApi.removeEventListener(event, refreshColumns)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection, gridApi, fetchGridConfiguration, getCollectionState, refreshColumns])

  const setupColumns = () => {
    const collectionState = getCollectionState(collection)
    if (!collectionState) {
      const cols = (gridApi.getColumnDefs() as ColDef[]).filter(column => column.headerName)
      const cleanedCols = removeSuppressMenuFromArray(cols)
      setColumns(cleanedCols)
    } else {
      setSelectedView(collectionState.selected)
      const savedCols = collectionState.state[collectionState.selected] || []
      const cleanedCols = removeSuppressMenuFromArray(savedCols)
      setColumns(cloneDeep(cleanedCols))
      setViews(
        Object.keys(collectionState.state).map(key => ({
          title: key,
          selected: collectionState.selected === key,
        }))
      )
    }
    setIsAllSelected(columns.every(column => !column.hide))
  }

  const createDefaultView = () => {
    const defaultColumns = gridApi.getAllGridColumns().map(value => ({
      ...value.getColDef(),
      colId: value.getColId(),
      pinned: value.getPinned(),
      sort: value.getSort(),
      hide: !value.isVisible(),
      width: value.getActualWidth(),
    }))
    const cleanedColumns = removeSuppressMenuFromArray(defaultColumns)

    const collectionState = {
      selected: 'par défaut',
      state: {
        'par défaut': cleanedColumns,
      },
      filter: { 'par défaut': {} },
    }

    updateGridConfiguration(collection, collectionState)
    saveGridConfiguration()
    setColumns(cleanedColumns)
    setViews([{ title: 'par défaut', selected: true }])
  }

  const handleCheckedChanged = (checked: boolean, column: ColDef) => {
    column.hide = !checked
    gridApi.setColumnsVisible([column.colId as string], checked)
    refreshColumns()
  }

  const handleSelectAll = (checked: boolean) => {
    const updatedColumns = columns.map(column => ({
      ...column,
      hide: !checked,
    }))
    setColumns(updatedColumns)
    gridApi.setColumnsVisible(columns.map(col => col.colId) as string[], checked)
    refreshColumns()
  }

  const handleResetState = () => {
    gridApi.resetColumnState()
    gridApi.setFilterModel({})
    refreshColumns()
  }

  const handleExportXls = () => {
    const filteredData: unknown[] = []
    const rowCount = gridApi.getDisplayedRowCount()

    for (let i = 0; i < rowCount; i++) {
      const rowNode = gridApi.getDisplayedRowAtIndex(i)
      if (rowNode) filteredData.push(rowNode.data)
    }

    exportAsXls(gridApi.getColumnDefs() as ColDef[], filteredData, collection)
  }

  const handleTogglePinned = (column: ColDef) => {
    const index = columns.findIndex(col => col.colId === column.colId)
    if (index === -1) return

    const pinned = columns[index].pinned
    const newPinned = pinned === 'left' ? 'right' : pinned === 'right' ? null : 'left'

    columns[index].pinned = newPinned
    gridApi.setColumnsPinned([column.colId as string], newPinned)
    refreshColumns()
  }

  const handleCreateView = (title: string) => {
    const gridFilters = gridApi.getFilterModel()
    const collectionState = getCollectionState(collection) || {
      selected: 'par défaut',
      state: { 'par défaut': [] },
      filter: { 'par défaut': {} },
    }

    const newColumns = gridApi.getAllGridColumns().map(value => ({
      ...value.getColDef(),
      colId: value.getColId(),
      pinned: value.getPinned(),
      sort: value.getSort(),
      hide: !value.isVisible(),
      width: value.getActualWidth(),
    }))
    const cleanedColumns = removeSuppressMenuFromArray(newColumns)

    const newState = {
      ...collectionState,
      filter: {
        ...collectionState.filter,
        [title]: gridFilters,
      },
      selected: title,
      state: {
        ...collectionState.state,
        [title]: cleanedColumns,
      },
    }

    updateGridConfiguration(collection, newState)
    saveGridConfiguration()
    setShowCreateView(false)
    setupColumns()
  }

  const handleChangeView = (view: string) => {
    const collectionState = getCollectionState(collection)
    if (!collectionState) return

    const newState = {
      ...collectionState,
      selected: view,
    }

    updateGridConfiguration(collection, newState)
    const viewColumns = collectionState.state[view] || []
    const cleanedColumns = removeSuppressMenuFromArray(viewColumns)
    setColumns(cleanedColumns)
    setSelectedView(view)

    // Update views array to reflect the selected view
    setViews(
      Object.keys(collectionState.state).map(key => ({
        title: key,
        selected: key === view,
      }))
    )

    gridApi.updateGridOptions({ columnDefs: [] })
    gridApi.updateGridOptions({ columnDefs: cleanedColumns })
    gridApi.setFilterModel(collectionState.filter?.[view] || {})
    saveGridConfiguration()
  }

  const handleDeleteView = (viewToDelete?: string) => {
    const collectionState = getCollectionState(collection)
    if (!collectionState) return

    const keys = Object.keys(collectionState.state)
    if (keys.length === 1) return

    const viewToDeleteName = viewToDelete || selectedView
    if (viewToDeleteName === 'par défaut') return

    const newState = {
      ...collectionState,
      selected: viewToDeleteName === selectedView ? 'par défaut' : selectedView,
      state: Object.fromEntries(
        Object.entries(collectionState.state).filter(([key]) => key !== viewToDeleteName)
      ),
      filter: Object.fromEntries(
        Object.entries(collectionState.filter || {}).filter(([key]) => key !== viewToDeleteName)
      ),
    }

    updateGridConfiguration(collection, newState)

    // If we deleted the currently selected view, switch to default
    if (viewToDeleteName === selectedView) {
      setSelectedView('par défaut')
      const defaultColumns = newState.state['par défaut'] || []
      const cleanedColumns = removeSuppressMenuFromArray(defaultColumns)
      setColumns(cleanedColumns)
      gridApi.updateGridOptions({ columnDefs: [] })
      gridApi.updateGridOptions({ columnDefs: cleanedColumns })
      gridApi.setFilterModel(newState.filter?.['par défaut'] || {})
    }

    // Update views array
    setViews(
      Object.keys(newState.state).map(key => ({
        title: key,
        selected: key === newState.selected,
      }))
    )

    saveGridConfiguration()
    setShowDeleteView(false)
    setupColumns()
  }

  const getColumnPinnedText = (column: ColDef): string => {
    return column.pinned ? (column.pinned === 'left' ? 'À gauche' : 'À droit') : 'Normal'
  }

  return (
    <div
      ref={containerRef}
      className="bg-[var(--color-gray-100)] border-l border-[var(--color-border)] flex flex-col relative transition-all duration-1000 flex-shrink-0"
    >
      {/* Columns visibility */}
      <div className="relative" ref={columnsMenuRef}>
        <button
          className="vertical flex items-center justify-center w-10 px-6 bg-[var(--color-gray-100)] hover:bg-[var(--color-gray-200)] transition-colors"
          onClick={() => setShowColumnsMenu(!showColumnsMenu)}
          title="Colonnes"
        >
          Colonnes
        </button>
        {showColumnsMenu && (
          <div
            className="absolute right-full top-0 mr-2 z-50 bg-white border border-[var(--color-border)] rounded-lg shadow-lg p-3 min-w-[300px] max-h-[55vh] overflow-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-2">
              <Checkbox
                label="Sélectionner tout"
                checked={isAllSelected}
                onChange={e => handleSelectAll(e.target.checked)}
                size="sm"
              />
            </div>
            {columns.map(column => (
              <div key={column.colId} className="mb-2">
                <Checkbox
                  label={column.headerName as string}
                  checked={!column.hide}
                  onChange={e => handleCheckedChanged(e.target.checked, column)}
                  size="sm"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Views */}
      {views.length > 0 && (
        <button
          className="vertical flex items-center justify-center w-10 px-6    bg-[var(--color-gray-100)] hover:bg-[var(--color-gray-200)] transition-colors"
          onClick={() => setShowViews(true)}
          title="Vues"
        >
          Vues
        </button>
      )}

      {/* Save view button */}
      {views.length > 0 && (
        <button
          className="flex items-center justify-center w-10 py-6 bg-[var(--color-gray-100)] hover:bg-[var(--color-gray-200)] transition-colors"
          onClick={() => setShowCreateView(true)}
          title="Enregistrer"
        >
          <FloppyDisk className="w-5 h-5" weight="regular" />
        </button>
      )}

      {/* Reset button */}
      <button
        className="flex items-center justify-center w-10 py-6 bg-[var(--color-gray-100)] hover:bg-[var(--color-gray-200)] transition-colors"
        onClick={handleResetState}
        title="Réinitialiser"
      >
        <ArrowClockwise className="w-5 h-5" weight="regular" />
      </button>

      {/* Export button */}
      <button
        className="flex items-center justify-center w-10 py-6 bg-[var(--color-gray-100)] hover:bg-[var(--color-gray-200)] transition-colors"
        onClick={handleExportXls}
        title="Exporter vers XLSX"
      >
        <Download className="w-5 h-5" weight="regular" />
      </button>

      {/* Pin columns */}
      <div className="relative" ref={pinMenuRef}>
        <div
          className="vertical bg-[var(--color-gray-100)] cursor-pointer hover:bg-[var(--color-gray-200)] transition-colors flex items-center justify-center"
          onClick={() => setShowPinMenu(!showPinMenu)}
        >
          <button
            className="flex items-center justify-center w-10 px-6"
            title="Épingler les colonnes"
          >
            <PushPin className="w-5 h-5" weight="regular" />
          </button>
        </div>
        {showPinMenu && (
          <div
            className="fixed z-50 bg-white border border-[var(--color-border)] rounded-lg shadow-lg p-3 min-w-[300px] overflow-auto"
            style={{
              right: containerRef.current
                ? `${window.innerWidth - containerRef.current.getBoundingClientRect().left + 8}px`
                : 'auto',
              bottom:
                gridBottomPosition > 0 ? `${window.innerHeight - gridBottomPosition}px` : 'auto',
              maxHeight: gridContentHeight > 0 ? `${gridContentHeight}px` : '55vh',
            }}
          >
            {columns
              .filter(col => !col.hide)
              .map(column => (
                <div key={column.colId} className="flex items-center justify-between mb-2">
                  <p className="p-0 m-0 text-sm text-[var(--color-text-primary)]">
                    {column.headerName}
                  </p>
                  <button
                    className="px-3 py-1 text-sm rounded-lg bg-[var(--color-gray-100)] hover:bg-[var(--color-gray-200)] transition-colors"
                    onClick={() => handleTogglePinned(column)}
                  >
                    {getColumnPinnedText(column)}
                  </button>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateView && (
        <CreateViewModal
          onClose={() => setShowCreateView(false)}
          onConfirm={handleCreateView}
          existingViews={views.map(v => v.title)}
        />
      )}

      {showViews && (
        <ViewsModal
          views={views}
          onClose={() => setShowViews(false)}
          onSelectView={handleChangeView}
          onDeleteView={viewName => {
            setViewToDelete(viewName)
            setShowViews(false)
            setShowDeleteView(true)
          }}
        />
      )}

      {showDeleteView && viewToDelete && (
        <DeleteViewModal
          viewName={viewToDelete}
          onClose={() => {
            setShowDeleteView(false)
            setViewToDelete(null)
          }}
          onConfirm={() => {
            handleDeleteView(viewToDelete)
            setViewToDelete(null)
          }}
        />
      )}
    </div>
  )
}
